import React, { useState, useEffect, FC } from "react";
import Content from "../../layout/content/Content";
import { Card } from "reactstrap";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
} from "../../components/Component";
import { Steps, Step } from "react-step-builder";
import { useForm, FormProvider } from "react-hook-form";
import { useLocation, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { message, Spin, Flex } from "antd";
import dayjs from "dayjs";

// Import step components
import WorkOrderGeneralDetails from "./workOrderCreation/workOrderGeneralDetails";
import WorkOrderDetails from "./workOrderCreation/workOrderDetails";
import WorkOrderGeneralConditions from "./workOrderCreation/workOrderGeneralConditions";
import WorkOrderPaymentConditions from "./workOrderCreation/workOrderPaymentConditions";
import WorkOrderPreview from "./workOrderCreation/workOrderPreview";
import Header_WorkOrder from "./workOrderCreation/Header_WorkOrder";

// Import types and data
import {
  WorkOrderFormData,
  WorkOrderStatus,
  ContractType,
  ContractWith,
  TaxApplicationLevel,
} from "./types";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";

const config = {
  before:
    Header_WorkOrder as React.ComponentType<{}> as () => React.ReactElement,
};

const WorkOrderCreationMain: FC = () => {
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  // Store original data for change tracking in edit mode
  const [originalEditData, setOriginalEditData] = useState<any>(null);

  // Check if editing or duplicating existing work order
  const editId = searchParams.get("edit");
  const isEditMode = !!editId;

  const formMethods = useForm<any>({
    mode: "onChange",
    defaultValues: {
      // General Information - Following FSD specs exactly
      id: "",
      workOrderNumber: "",
      linkedWorkRequest: "",
      linkedTender: "",
      contractDate: null,
      workOrderTitle: "",
      budgetHeaderId: "", // Add budgetHeaderId to form defaults
      budgetHeaderName: "", // Budget name for display
      workOrderDescription: "",
      siteId: "",
      siteName: "",
      projectId: "",
      projectName: "",
      contractType: "" as ContractType,
      contractWith: "" as ContractWith,
      contractorId: "",
      contractorName: "",
      workOrderOwner: "",
      workOrderOwnerName: "",
      plannedStartDate: null,
      plannedEndDate: null,
      approvalRequired: "false", // String for form handling
      enableTax: "false", // String for form handling
      taxApplicationLevel: "" as TaxApplicationLevel,

      // Work Order Information
      selectedSource: undefined,
      activityId: "",
      workCategoryId: "",
      workBoqId: "",
      workLevel: "", // Work Level Settings for New Work Line source
      workType: "QTY", // Work Type for New Work Line source (default: QTY)
      workOrderLines: [],

      // General Conditions
      generalConditions: [],

      // Payment Conditions
      paymentConditions: {
        mobilizationAdvance: {
          isPercentOfContract: false,
          interestCharged: false,
          recoveryType: "Each RA Bill" as any,
        },
        retentionAmount: {
          isPercentOfRABill: false,
          releaseDate: "",
        },
        onHoldAmount: {
          isPercentOfContract: false,
          releaseDate: "",
        },
        payOnWorkCompletion: false,
      },

      // Internal fields
      status: WorkOrderStatus.Draft,
      type: isEditMode ? "edit" : "create",
      createdBy: "Current User",
    },
  });

  const { watch, setValue, reset } = formMethods;
  const location = useLocation();

  // Get flowType from URL to preserve it in navigation
  const flowType = searchParams.get("flowType");
  const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);

    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  useEffect(() => {
    // Load existing data if editing or duplicating
    if (editId) {
      loadWorkOrderData(editId);
    }
  }, [editId]);

  const viewChange = () => {
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const loadWorkOrderData = async (id: string) => {
    try {
      setLoading(true);

      if (!id) {
        message.error("Work Order ID is required");
        return;
      }

      console.log("Loading Work Order for edit with ID:", id);

      // Call the actual API
      const response: any = await workOrderApis.getWorkOrderById(id);

      if (response && response.status && response.data) {
        const apiData = response.data;
        console.log("Loaded Work Order data for edit:", apiData);

        // Map work order lines with proper structure for form
        console.log(
          "API Work Order Lines for budget mapping:",
          apiData.workOrderLines
        );
        const mappedWorkOrderLines =
          apiData.workOrderLines?.map((line: any, index: number) => {
            console.log(`Mapping line ${index} budget data:`, {
              budgetLineId: line.budgetLineId,
              budgetLineName: line.budgetLineName,
              budgetAllocation: line.budgetAllocation,
              sgstRate: line.sgstRate,
              cgstRate: line.cgstRate,
              igstRate: line.igstRate,
            });
            return {
              id: line.workOrderLineId?.toString() || `line_${index}`,
              workOrderLineId: line.workOrderLineId, // Store database ID for deletion tracking
              lineNumber: line.sequence?.toString() || (index + 1).toString(),
              workCategory: line.workCategoryId?.toString() || "", // Use ID for form field
              workCategoryName: line.workCategoryName || "", // Keep name for display
              subCategory: "",
              taskTitle: line.taskTitle || "",
              taskDescription: line.description || "",
              quantity: line.quantity || 0,
              uom: line.uomId?.toString() || "", // Use ID for form field
              uomName: line.uomName || "", // Keep name for display
              weightagePercentage: line.weightagePct || 0,
              rate: line.rate || 0,
              taskRate: line.targetRate || 0,
              boqRate: line.targetRate || 0,
              remarks: line.remarks || "",
              taxType: line.taxId?.toString() || "",
              taxTypeName: line.taxName || "",
              // Calculate total tax percentage from GST rates
              taxPercentage:
                line.taxRate ||
                (line.sgstRate || 0) +
                (line.cgstRate || 0) +
                (line.igstRate || 0),
              taxAmount: line.taxAmount || 0,
              totalAmount: line.lineValue || 0,
              source:
                line.sourceType === "Project Tasks"
                  ? "Project Management"
                  : line.sourceType === "Work BOQ"
                    ? "Work BOQ"
                    : "New Work Line", // Default to New Work Line for any other values
              sourceId: line.sourceId?.toString() || "",
              sectionId: "",
              executedQuantity: line.allocatedQty || 0,
              remainingQuantity:
                (line.quantity || 0) - (line.allocatedQty || 0),
              completionPercentage: 0,
              // Budget allocation fields for edit mode
              budgetLineId: line.budgetLineId || 0,
              budgetLineName: line.budgetLineName || "",
              budgetAllocation: line.budgetAllocation || 0,
              // GST specific fields for tax display
              sgstRate: line.sgstRate || 0,
              cgstRate: line.cgstRate || 0,
              igstRate: line.igstRate || 0,
              // Work Level Settings location fields (map from API field names)
              // API uses: siteStructureUnitId, floorNo, siteStructureSubUnitId, locationId
              // Internal uses: towerVilla, floorName, flatName, locationId
              workLevel: apiData.segmentLevel || line.segmentLevel || "",
              towerVilla:
                line.siteStructureUnitId?.toString() || line.towerVilla || "",
              towerVillaName:
                line.towerVillaName || line.siteStructureUnitName || "",
              floorName: line.floorNo || line.floorName || "",
              flatName:
                line.siteStructureSubUnitId?.toString() || line.flatName || "",
              flatNameDisplay:
                line.flatNameDisplay || line.siteStructureSubUnitName || "",
              locationId: line.locationId?.toString() || "",
              locationName: line.locationName || "",
              // Work type field - default to QTY if not specified (for backward compatibility)
              workType: apiData.workType || "QTY",
              // Schedule breakdown stages
              stages:
                line.stages?.map((stage: any) => ({
                  workOrderLineStageId: stage.workOrderLineStageId,
                  stageTitle: stage.stageTitle || "",
                  splitPercentage: stage.splitPercentage || 0,
                })) || [],
              // Map files from line.dms or line.filesData to files array
              files:
                (line.dms || line.filesData || []).map((file: any) => ({
                  uid:
                    file.docId?.toString() ||
                    file.dmsId?.toString() ||
                    file.fileId?.toString() ||
                    `${Date.now()}-${Math.random()}`,
                  name:
                    file.fileName ||
                    file.originalFileName ||
                    `File ${file.docId || file.dmsId || file.fileId}`,
                  status: "done" as const,
                  url: file.fileUrl || file.url || file.filePath || undefined,
                  filePath:
                    file.filePath || file.fileUrl || file.url || undefined,
                  originFileObj: null, // For existing files, we don't have File object
                  response: {
                    fileId: file.docId || file.dmsId || file.fileId,
                    fileName: file.fileName || file.originalFileName,
                    fileSize: file.fileSize || 0,
                    fileType: file.fileType || file.mimeType || "",
                    uploadDate:
                      file.uploadDate ||
                      file.createdDate ||
                      new Date().toISOString(),
                    filePath:
                      file.filePath || file.fileUrl || file.url || undefined,
                  },
                  // Store API file data for reference
                  apiFileData: file,
                })) || [],
              scheduledStartDate: line.scheduledStartDate || line.plannedStartDate || "",
              scheduledEndDate: line.scheduledEndDate || line.plannedEndDate || "",
            };
          }) || [];

        console.log(
          "Final mapped work order lines with budget data:",
          mappedWorkOrderLines
        );

        // Map general conditions - use conditionCode as ID to match predefined conditions
        const mappedGeneralConditions =
          apiData.workOrderGeneralConditions?.map(
            (condition: any, index: number) => ({
              id: condition.conditionCode || `condition_${index}`, // Use conditionCode as ID to match predefined
              workOrderConditionId: condition.workOrderConditionId, // Store database ID for deletion tracking
              conditionName: condition.conditionCode || "",
              conditionContent: condition.content || "",
              isActive: true,
              order: index + 1, // Use proper order
              attachments: [],
            })
          ) || [];

        console.log("Mapped General Conditions:", mappedGeneralConditions);

        // Map payment conditions - convert array format to object format for form
        const mappedPaymentConditions = {
          mobilizationAdvance: {
            workOrderPaymentTermId: null,
            isPercentOfContract: false,
            percentageOfContract: 0,
            advanceValue: 0,
            interestCharged: false,
            interestRate: 0,
            recoveryPercentRate: 0,
            recoveryCapPercent: 0,
            recoveryType: "Each RA Bill" as any,
          },
          retentionAmount: {
            workOrderPaymentTermId: null,
            isPercentOfRABill: false,
            percentageOfRABill: 0,
            retentionValue: 0,
            dlpAmount: 1,
            dlpUnit: "years" as const,
            releaseDate: null,
          },
          onHoldAmount: {
            workOrderPaymentTermId: null,
            isPercentOfContract: false,
            percentageOfContract: 0,
            holdAmountValue: 0,
            dlpAmount: 1,
            dlpUnit: "years" as const,
            releaseDate: null,
          },
          payOnWorkCompletion: false,
        };

        // Map API payment conditions array to form object structure
        if (
          apiData.workOrderPaymentConditions &&
          Array.isArray(apiData.workOrderPaymentConditions)
        ) {
          apiData.workOrderPaymentConditions.forEach((condition: any) => {
            switch (condition.conditionType) {
              case "Mobilization":
                mappedPaymentConditions.mobilizationAdvance = {
                  workOrderPaymentTermId: condition.workOrderPaymentTermId, // Store database ID for updates
                  isPercentOfContract: condition.isPercent || false,
                  percentageOfContract: condition.percentValue || 0,
                  advanceValue: condition.amountValue || 0,
                  interestCharged: condition.interestCharged || false,
                  interestRate: condition.interestRate || 0,
                  recoveryPercentRate: condition.recoveryPercentRate || 0,
                  recoveryCapPercent: condition.recoveryCapPercent || 0,
                  recoveryType:
                    condition.recoveryType === "On Every RA Bill"
                      ? "Each RA Bill"
                      : condition.recoveryType || "Each RA Bill",
                };
                break;
              case "Retention":
                mappedPaymentConditions.retentionAmount = {
                  workOrderPaymentTermId: condition.workOrderPaymentTermId, // Store database ID for updates
                  isPercentOfRABill: condition.isPercent || false,
                  percentageOfRABill: condition.percentValue || 0,
                  retentionValue: condition.amountValue || 0,
                  dlpAmount: 1,
                  dlpUnit: "years" as const,
                  releaseDate: null, // Reset for duplicate
                };
                break;
              case "On Hold":
                mappedPaymentConditions.onHoldAmount = {
                  workOrderPaymentTermId: condition.workOrderPaymentTermId, // Store database ID for updates
                  isPercentOfContract: condition.isPercent || false,
                  percentageOfContract: condition.percentValue || 0,
                  holdAmountValue: condition.amountValue || 0,
                  dlpAmount: 1,
                  dlpUnit: "years" as const,
                  releaseDate: null, // Reset for duplicate
                };
                break;
            }
          });
        }

        // Set form values from API data
        setValue("id", apiData.workOrderId?.toString() || editId); // Set work order ID for editing
        setValue("workOrderNumber", apiData.workOrderNumber || "");
        setValue("linkedWorkRequest", ""); // Not provided in API response
        setValue("linkedTender", ""); // Not provided in API response
        setValue(
          "contractDate",
          apiData.contractDate ? dayjs(apiData.contractDate) : null
        );
        setValue("workOrderTitle", apiData.workOrderTitle || "");
        setValue("workOrderDescription", apiData.description || "");
        setValue("siteId", apiData.siteId?.toString() || "");
        setValue("siteName", apiData.siteName || "");
        setValue("projectId", apiData.projectId?.toString() || "");
        setValue("projectName", apiData.projectName || "");
        // Map contract type - API returns "Construction" but enum expects "Construction Work"
        const mappedContractType =
          apiData.contractType === "Construction"
            ? ContractType.Construction
            : (apiData.contractType as ContractType) ||
            ContractType.Construction;
        setValue("contractType", apiData.contractType);

        // Map contract with - API returns "GC" but enum expects "General Contractor"
        const mappedContractWith =
          apiData.contractWith === "GC"
            ? ContractWith.GeneralContractor
            : (apiData.contractWith as ContractWith) ||
            ContractWith.GeneralContractor;
        setValue("contractWith", apiData.contractWith);
        setValue("contractorId", apiData.contractorId?.toString() || "");
        setValue("contractorName", apiData.contractorName || "");
        setValue("workOrderOwner", apiData.workOrderOwnerId?.toString() || "");
        setValue("workOrderOwnerName", apiData.workOrderOwnerName || "");
        setValue(
          "plannedStartDate",
          apiData.plannedStartDate ? dayjs(apiData.plannedStartDate) : null
        );
        setValue(
          "plannedEndDate",
          apiData.plannedEndDate ? dayjs(apiData.plannedEndDate) : null
        );
        setValue(
          "approvalRequired",
          apiData.approvalRequired ? "true" : "false"
        );
        setValue("enableTax", apiData.enableTax ? "true" : "false");
        setValue(
          "taxApplicationLevel",
          apiData.taxLevel || ("" as TaxApplicationLevel)
        );

        // Map tax-related fields
        setValue("headerTaxType", apiData.headerTaxId?.toString() || "");
        setValue("tdsTaxType", apiData.tdsTaxId?.toString() || "");
        setValue("tcsTaxType", apiData.tcsTaxId?.toString() || "");
        setValue("budgetId", apiData.budgetId?.toString() || "");
        setValue("budgetHeaderId", apiData.budgetHeaderId?.toString() || "");
        setValue("workLevel", apiData.segmentLevel || "");
        setValue("workType", apiData.workType || "QTY"); // Default to QTY for backward compatibility
        // Set selected source based on API response
        if (apiData.sourceType) {
          const selectedSource =
            apiData.sourceType === "Project Tasks"
              ? "Project Management"
              : apiData.sourceType === "Work BOQ"
                ? "Work BOQ"
                : "New Work Line";
          setValue("selectedSource", selectedSource);
        }

        setValue("workOrderLines", mappedWorkOrderLines);
        setValue("generalConditions", mappedGeneralConditions);
        console.log("Set generalConditions in form:", mappedGeneralConditions);
        setValue("paymentConditions", mappedPaymentConditions);
        setValue(
          "status",
          (apiData.status as WorkOrderStatus) || WorkOrderStatus.Draft
        );
        setValue("type", "edit");

        // Load files from API response (dms or files array)
        const apiFiles = apiData.dms || apiData.files || [];
        console.log("Loading files from API:", apiFiles);

        // Convert API file format to form format and distribute by source type
        // Since files are at work order level, we'll show all files in all source types
        // when viewing existing work order
        const fileListFormat = apiFiles.map((file: any) => ({
          uid:
            file.docId?.toString() ||
            file.dmsId?.toString() ||
            file.fileId?.toString() ||
            `${Date.now()}-${Math.random()}`,
          name:
            file.fileName ||
            file.originalFileName ||
            `File ${file.docId || file.dmsId || file.fileId}`,
          status: "done" as const,
          url: file.fileUrl || file.url || file.filePath || undefined,
          filePath: file.filePath || file.fileUrl || file.url || undefined,
          originFileObj: null, // For existing files, we don't have File object
          response: {
            fileId: file.docId || file.dmsId || file.fileId,
            fileName: file.fileName || file.originalFileName,
            fileSize: file.fileSize || 0,
            fileType: file.fileType || file.mimeType || "",
            uploadDate:
              file.uploadDate || file.createdDate || new Date().toISOString(),
            filePath: file.filePath || file.fileUrl || file.url || undefined,
          },
          // Store API file data for reference
          apiFileData: file,
        }));

        // Store files based on selected source type
        // If source type matches, store files in that category
        // Otherwise, store in all categories for viewing
        const selectedSource =
          apiData.sourceType === "Project Tasks"
            ? "Project Management"
            : apiData.sourceType === "Work BOQ"
              ? "Work BOQ"
              : "New Work Line";

        if (selectedSource === "Project Management") {
          setValue("projectManagementFiles" as any, fileListFormat);
        } else if (selectedSource === "Work BOQ") {
          setValue("workBoqFiles" as any, fileListFormat);
        } else {
          setValue("newWorkLineFiles" as any, fileListFormat);
        }

        // Also store in workOrderDetails component state if needed
        // This will be handled by the component itself when it receives the data

        // Capture original data for deletion tracking and change detection immediately after loading
        const originalDataForTracking = {
          // Form fields for change detection
          id: apiData.workOrderId?.toString() || editId,
          workOrderNumber: apiData.workOrderNumber || "",
          linkedWorkRequest: "", // Not provided in API response
          linkedTender: "", // Not provided in API response
          contractDate: apiData.contractDate
            ? dayjs(apiData.contractDate)
            : null,
          workOrderTitle: apiData.workOrderTitle || "",
          workOrderDescription: apiData.description || "",
          siteId: apiData.siteId?.toString() || "",
          siteName: apiData.siteName || "",
          projectId: apiData.projectId?.toString() || "",
          projectName: apiData.projectName || "",
          contractType: apiData.contractType,
          contractWith: apiData.contractWith,
          contractorId: apiData.contractorId?.toString() || "",
          contractorName: apiData.contractorName || "",
          workOrderOwner: apiData.workOrderOwnerId?.toString() || "",
          workOrderOwnerName: apiData.workOrderOwnerName || "",
          plannedStartDate: apiData.plannedStartDate
            ? dayjs(apiData.plannedStartDate)
            : null,
          plannedEndDate: apiData.plannedEndDate
            ? dayjs(apiData.plannedEndDate)
            : null,
          approvalRequired: apiData.approvalRequired ? "true" : "false",
          enableTax: apiData.enableTax ? "true" : "false",
          taxApplicationLevel: apiData.taxLevel || ("" as TaxApplicationLevel),
          headerTaxType: apiData.headerTaxId?.toString() || "",
          tdsTaxType: apiData.tdsTaxId?.toString() || "",
          tcsTaxType: apiData.tcsTaxId?.toString() || "",
          budgetId: apiData.budgetId?.toString() || "",
          budgetHeaderId: apiData.budgetHeaderId?.toString() || "",
          workLevel: apiData.segmentLevel || "",
          workType: apiData.workType || "QTY",
          status: (apiData.status as WorkOrderStatus) || WorkOrderStatus.Draft,
          type: "edit",

          // Arrays for deletion tracking
          workOrderLines: [...mappedWorkOrderLines],
          generalConditions: [...mappedGeneralConditions],
          paymentConditions: { ...mappedPaymentConditions },
          // Store original files for deletion tracking
          // Files are stored in each line's files array, so they're already in workOrderLines
        };
        console.log("=== CAPTURING COMPLETE ORIGINAL DATA FOR TRACKING ===");
        console.log("Original Complete Data:", originalDataForTracking);
        setOriginalEditData(originalDataForTracking);

        message.success("Work order data loaded successfully");
      } else {
        throw new Error(
          response?.message || "Failed to fetch Work Order details"
        );
      }
    } catch (error: any) {
      console.error("Error loading work order:", error);
      message.error("Failed to load work order data");
    } finally {
      setLoading(false);
    }
  };

  const getPageTitle = () => {
    if (isEditMode) return `Edit Work Order - ${watch("workOrderNumber")}`;
    return "New Work Order";
  };

  const getPageSubtitle = () => {
    if (isEditMode) return "Modify existing work order details";
    return "Create a new work order following the five-step process";
  };

  return (
    <>
      <Head title="Work Order Creation" />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}

              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">{getPageTitle()}</BlockTitle>
                    {watch("workOrderNumber") && (
                      <div className="mt-2">
                        <span
                          className="badge badge-outline-primary"
                          style={{ color: "black" }}
                        >
                          WO #: {watch("workOrderNumber")}
                        </span>
                        {watch("status") && (
                          <span
                            className="badge badge-outline-info ms-2"
                            style={{ color: "black" }}
                          >
                            Status: {watch("status")}
                          </span>
                        )}
                      </div>
                    )}
                  </BlockHeadContent>

                  <BlockHeadContent>
                    <Link to={`/view-work-orders${flowTypeQuery}`}>
                      <Button
                        outline
                        className="bg-white d-none d-sm-inline-flex"
                      >
                        <Icon name="arrow-left"></Icon>
                        <span>Back to List</span>
                      </Button>
                      <Button
                        outline
                        className="btn-icon bg-white d-inline-flex d-sm-none"
                      >
                        <Icon name="arrow-left"></Icon>
                      </Button>
                    </Link>
                  </BlockHeadContent>

                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${sm ? "active" : ""
                        }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <Block className="mt-1">
                {loading ? (
                  <Flex align="center" gap="middle" className="pageLoader">
                    <Spin size="large" className="m-auto" />
                    {/* <span>Loading work order data...</span> */}
                  </Flex>
                ) : (
                  <div className="nk-wizard nk-wizard-simple is-alter wizard clearfix">
                    <FormProvider {...formMethods}>
                      <Steps config={config}>
                        {/* Step 1: General Information (FSD Section A) */}
                        <Step component={WorkOrderGeneralDetails} />

                        {/* Step 2: Work Order Information (FSD Section B) */}
                        <Step component={WorkOrderDetails} />

                        {/* Step 3: General Conditions (FSD Section C) */}
                        <Step component={WorkOrderGeneralConditions} />

                        {/* Step 4: Payment Conditions (FSD Section D) */}
                        <Step component={WorkOrderPaymentConditions} />

                        {/* Step 5: Preview & Submit (FSD Section E) */}
                        <Step
                          component={(props: any) => (
                            <WorkOrderPreview
                              {...props}
                              originalEditData={originalEditData}
                            />
                          )}
                        />
                      </Steps>
                    </FormProvider>
                  </div>
                )}
              </Block>
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default WorkOrderCreationMain;
